import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, HandCoins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { celebrate } from "@/components/celebrate";
import { api, money, formatApiError } from "@/lib/api";

const selectCls = "w-full rounded-xl border border-[#e4d8c4] bg-white px-3 py-2.5 text-sm text-[#00334a]";
const METHODS = ["givebutter", "check", "cash", "in-kind", "other"];

export default function DonationsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [teams, setTeams] = useState([]);
  const [donations, setDonations] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ campaign_id: "", team_id: "", supporter_id: "", amount: "", donor_name: "", method: "givebutter", note: "" });

  useEffect(() => { api.get("/campaigns").then(({ data }) => setCampaigns(data)); }, []);

  const loadDonations = useCallback(() => {
    api.get("/donations").then(({ data }) => setDonations(data)).catch(() => {});
  }, []);
  useEffect(() => { loadDonations(); }, [loadDonations]);

  useEffect(() => {
    if (form.campaign_id) {
      api.get(`/teams?campaign_id=${form.campaign_id}`).then(({ data }) => setTeams(data));
    } else setTeams([]);
  }, [form.campaign_id]);

  const openNew = () => {
    setForm({ campaign_id: campaigns[0]?.id || "", team_id: "", supporter_id: "", amount: "", donor_name: "", method: "givebutter", note: "" });
    setOpen(true);
  };

  const selectedTeam = teams.find((t) => t.id === form.team_id);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        campaign_id: form.campaign_id,
        team_id: form.team_id || null,
        supporter_id: form.supporter_id || null,
        amount: Number(form.amount),
        donor_name: form.donor_name,
        method: form.method,
        note: form.note,
      };
      const { data } = await api.post("/donations", payload);
      toast.success("Donation recorded");
      if (data.milestones_hit?.length) {
        celebrate();
        toast.success(`🎉 Milestone reached: ${Math.max(...data.milestones_hit)}%! Team emails sent.`);
      }
      setOpen(false);
      loadDonations();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this donation record?")) return;
    await api.delete(`/donations/${id}`);
    toast.success("Deleted");
    loadDonations();
  };

  const nameFor = (list, id) => list.find((x) => x.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="admin-donations">
      <div className="flex items-center justify-between">
        <div>
          <span className="hf-eyebrow">Financial flow</span>
          <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#00334a]">Donations</h1>
          <p className="mt-1 text-sm text-[#7c7a6a]">Record offline gifts and Givebutter totals to keep standings accurate.</p>
        </div>
        <Button onClick={openNew} className="rounded-full hf-btn-coral font-bold" data-testid="admin-log-donation-button">
          <Plus className="mr-2 h-4 w-4" /> Log donation
        </Button>
      </div>

      {donations.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#e4d8c4] bg-white p-12 text-center">
          <HandCoins className="mx-auto h-10 w-10 text-[#e4d8c4]" />
          <p className="mt-3 font-semibold text-[#00334a]">No donations recorded yet</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#e4d8c4] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[#f8f3e9] text-left text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">
              <tr>
                <th className="px-4 py-3">Donor</th><th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {donations.map((d) => (
                <tr key={d.id} className="border-t border-[#efe6d6]" data-testid={`donation-row-${d.id}`}>
                  <td className="px-4 py-3 font-semibold text-[#00334a]">{d.donor_name || "Anonymous"}</td>
                  <td className="px-4 py-3 capitalize text-[#7c7a6a]">{d.method}</td>
                  <td className="px-4 py-3 text-[#7c7a6a]">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right font-heading font-bold text-[#65949c]">{money(d.amount)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => remove(d.id)} className="h-8 w-8 text-[#c97049]" data-testid={`donation-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="admin-log-donation-form">
          <DialogHeader><DialogTitle className="font-heading">Log a donation</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Campaign</Label>
              <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value, team_id: "", supporter_id: "" })} className={selectCls} required data-testid="donation-campaign-select">
                <option value="">Select campaign…</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Team</Label>
                <select value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value, supporter_id: "" })} className={selectCls} data-testid="donation-team-select">
                  <option value="">— none —</option>
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Credit supporter</Label>
                <select value={form.supporter_id} onChange={(e) => setForm({ ...form, supporter_id: e.target.value })} className={selectCls} data-testid="donation-supporter-select">
                  <option value="">— team-wide —</option>
                  {(selectedTeam?.members || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Amount ($)</Label><Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="donation-amount-input" /></div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className={`${selectCls} capitalize`} data-testid="donation-method-select">
                  {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Donor name</Label><Input value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} placeholder="Optional" data-testid="donation-donor-input" /></div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></div>
            <DialogFooter><Button type="submit" disabled={busy} className="rounded-full hf-btn-coral font-bold" data-testid="donation-save-button">{busy ? "Saving…" : "Record donation"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
